const BASIC_ROOM_SETTINGS = Object.freeze({
  powerupIdsAlways: [],
  fps: 50,
  // Restored from the pre-widefield configuration in git history.
  fieldWidth: 100,
  fieldHeight: 100,
  firstTo: 100,
  pointsPerSurvive: 1,
  pointsPerKill: 0,
  // Keep the same Basic gameplay tuning on the square field. CurveCrash GS+
  // uses 103% of the 35 px turn-radius base, which rounds to 36.1 px.
  curveSize: 100,
  curveTouchiness: 100,
  curveSpeed: 100,
  // 86% of the 35 px Curve base = 30.1 px. Chosen so that ONE SPEED stack
  // (which multiplies the radius by 1.2) turns in 36.1 px -- the radius the
  // game handled unboosted until now. Picking up a boost therefore never
  // enlarges your turning footprint beyond what you already cope with; the
  // whole ladder shifts down one rung.
  //
  // Measured before shipping, not assumed: 48 paired seeds, 2 bots, arms at
  // 80/86/90/103. Nothing separated them -- deathless 32/48 here against 33/48
  // at 103, and every paired difference under 1.4 sigma -- so this is a feel
  // choice the bot does not punish, not a survival win. Departs from the GS+
  // parity e4091d9 set; CurveCrash pairs its 103 with curveSpeed 130 and
  // fieldWidth 110, neither of which we took.
  turnRadius: 86,
  holeSize: 100,
  holeFrequency: 100,
  powerupFrequency: 100,
  powerupRadius: 100,
  isEraseDead: false
});

const CURVE_BASE_VALUES = Object.freeze({
  fieldSize: 720,
  curveDraw: 0.87,
  curveSize: 6,
  curveTouchiness: 1.17,
  curveSpeed: 90,
  turnRadius: 35,
  holeSize: 16,
  holeFrequency: 0.5,
  powerupFrequency: 0.75,
  powerupRadius: 20
});

// Per-player turn radius uses the same percentage scale as the original room
// tuning. The server owns and validates it; clients only request a value.
// Below 60%, a 100 ms tap turns roughly 25 degrees and the 42 px circle is
// already only about six trail widths across. Above 160%, one SPEED stack
// expands the 112 px circle to 134.4 px, leaving too little room to correct.
const PLAYER_TURN_RADIUS_MIN_PERCENT = 60;
const PLAYER_TURN_RADIUS_MAX_PERCENT = 160;

const BASIC_POWERUP_REFERENCE_WEIGHTS = Object.freeze({
  SPEED: 100,
  CLEAR: 50
});

const GEM_MODE_POWERUP_WEIGHTS = Object.freeze({
  GEM: 120,
  SPEED: 50,
  CLEAR: 30
});

function roundTo(value, decimals = 0) {
  const factor = 10 ** decimals;
  return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
}

function percentValue(setting, base, decimals) {
  return roundTo((Number(setting) / 100) * base, decimals);
}

function normalizePlayerTurnRadiusPercent(value, fallback = BASIC_ROOM_SETTINGS.turnRadius) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(
    PLAYER_TURN_RADIUS_MIN_PERCENT,
    Math.min(PLAYER_TURN_RADIUS_MAX_PERCENT, Math.round(parsed))
  );
}

function playerTurnRadiusForPercent(value) {
  return percentValue(
    normalizePlayerTurnRadiusPercent(value),
    CURVE_BASE_VALUES.turnRadius,
    1
  );
}

function weightTotal(weights) {
  return Object.values(weights).reduce((sum, weight) => sum + weight, 0);
}

// The field grows for a crowd, and only for a crowd.
//
// 720x720 is the field every match has played on and stays the default. Past
// five players it is simply too tight -- this is a gem-collection game, so the
// arena has to hold pickups AND the trails of everyone chasing them, and a
// square that works for a duel does not work for eight.
//
// The wide value is not invented: 1064 is the game's own pre-refactor
// widescreen width (147.8% of the 720 base), restored here from the
// configuration that commit 3f5fc24 replaced with the square field. Height
// stays at 720, so the growth is horizontal -- which is where the screen has
// room, the arena being height-bound.
//
// NOT CF2's rule. CF2/CurveCrash scale BOTH axes by sqrt(n/8) and only ever
// shrink (see cc-source, _SafePkg_1/_SafeCls_48.as). That was tried and it does
// not suit gem mode: it made small games tiny rather than big games roomy.
const WIDE_FIELD_MIN_PLAYERS = 6;
const WIDE_FIELD_WIDTH_PERCENT = 147.8;

function fieldSizeForPlayers(playerCount) {
  const participants = Math.max(1, Math.floor(Number(playerCount) || 0));
  const wide = participants >= WIDE_FIELD_MIN_PLAYERS;
  return {
    width: wide
      ? Math.round((WIDE_FIELD_WIDTH_PERCENT / 100) * CURVE_BASE_VALUES.fieldSize)
      : fieldWidth,
    height: fieldHeight
  };
}

function matchTarget(playerCount) {
  const participants = Math.max(1, Math.floor(Number(playerCount) || 0));
  return Math.max(1, Math.round(10 * (participants - 1) * (BASIC_ROOM_SETTINGS.firstTo / 100)));
}

const fieldWidth = Math.round((BASIC_ROOM_SETTINGS.fieldWidth / 100) * CURVE_BASE_VALUES.fieldSize);
const fieldHeight = Math.round((BASIC_ROOM_SETTINGS.fieldHeight / 100) * CURVE_BASE_VALUES.fieldSize);
const curveSize = percentValue(BASIC_ROOM_SETTINGS.curveSize, CURVE_BASE_VALUES.curveSize, 1);
const curveTouchiness = percentValue(
  BASIC_ROOM_SETTINGS.curveTouchiness,
  CURVE_BASE_VALUES.curveTouchiness,
  2
);
const trailSize = roundTo(curveSize + CURVE_BASE_VALUES.curveDraw, 2);
const collisionSize = roundTo(trailSize - curveTouchiness, 2);
const powerupFrequencyPerSecond = percentValue(
  BASIC_ROOM_SETTINGS.powerupFrequency,
  CURVE_BASE_VALUES.powerupFrequency,
  2
);
const gemModePowerupAmountScale = weightTotal(GEM_MODE_POWERUP_WEIGHTS)
  / weightTotal(BASIC_POWERUP_REFERENCE_WEIGHTS);

const BASIC_BALANCE = Object.freeze({
  fieldWidth,
  fieldHeight,
  firstToPercent: BASIC_ROOM_SETTINGS.firstTo,
  matchTargetPointsPerOpponent: 10,
  pointsPerSurvive: BASIC_ROOM_SETTINGS.pointsPerSurvive,
  pointsPerKill: BASIC_ROOM_SETTINGS.pointsPerKill,
  eraseDeadLines: BASIC_ROOM_SETTINGS.isEraseDead,
  alwaysActivePowerups: BASIC_ROOM_SETTINGS.powerupIdsAlways,
  fps: BASIC_ROOM_SETTINGS.fps,
  tickMs: 1000 / BASIC_ROOM_SETTINGS.fps,
  curveSize,
  curveDraw: CURVE_BASE_VALUES.curveDraw,
  curveTouchiness,
  trailSize,
  collisionSize,
  bikeSpeed: percentValue(BASIC_ROOM_SETTINGS.curveSpeed, CURVE_BASE_VALUES.curveSpeed, 1),
  turnRadius: percentValue(BASIC_ROOM_SETTINGS.turnRadius, CURVE_BASE_VALUES.turnRadius, 1),
  holeSize: percentValue(BASIC_ROOM_SETTINGS.holeSize, CURVE_BASE_VALUES.holeSize, 1),
  holeFrequencyPerSecond: percentValue(
    BASIC_ROOM_SETTINGS.holeFrequency,
    CURVE_BASE_VALUES.holeFrequency,
    2
  ),
  powerupFrequencyPerSecond,
  powerupSpawnFrequencyPerSecond: roundTo(powerupFrequencyPerSecond * gemModePowerupAmountScale, 3),
  powerupRadius: percentValue(BASIC_ROOM_SETTINGS.powerupRadius, CURVE_BASE_VALUES.powerupRadius, 1),
  speedPowerupDurationSeconds: 3,
  // Curve Fever 2 and CurveCrash both apply these multiplicatively per pickup
  // and undo them per expiry (`speed *= 2` / `speed /= 2`), so concurrent boosts
  // compound: n stacks is 2^n speed and 1.2^n turn radius. Verified in both
  // sources -- CF2's PU_S_SPEED (cc-source/curvefever2,
  // _SafePkg_41/_SafeCls_206.as) does exactly `speed * 2; radius *= 1.2` on
  // activate and the inverse on deactivate, with its sibling PU_S_SLOW doing
  // `speed / 2; radius /= 1.5`; speed and radius always move together there.
  // Neither caps it -- each powerup on the field is its own object with
  // its own timer, and the only guard is on that instance, never on what the
  // player already holds. Infinity keeps parity. The steps are steep on a 720 px
  // field (1 stack 180 px/s, 2 stacks 360, 3 stacks 720) but it is self-limiting:
  // at those speeds you reach a wall almost immediately. Set a positive integer
  // here to reintroduce a cap.
  speedPowerupSpeedMultiplier: 2,
  speedPowerupTurnRadiusMultiplier: 1.2,
  speedPowerupMaxStacks: Infinity
});

export {
  BASIC_BALANCE,
  BASIC_POWERUP_REFERENCE_WEIGHTS,
  BASIC_ROOM_SETTINGS,
  CURVE_BASE_VALUES,
  GEM_MODE_POWERUP_WEIGHTS,
  PLAYER_TURN_RADIUS_MAX_PERCENT,
  PLAYER_TURN_RADIUS_MIN_PERCENT,
  fieldSizeForPlayers,
  matchTarget,
  normalizePlayerTurnRadiusPercent,
  playerTurnRadiusForPercent,
  percentValue,
  roundTo,
  weightTotal
};
